import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { createParty } from "@/app/services/partyService";
import { PartyCreate } from "@/app/types/party";

const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  phone: z.string().optional().nullable(),
});

type QuickPartyFormValues = z.infer<typeof formSchema>;

interface QuickPartyFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (partyId: number, party: any) => void;
}

const QuickPartyForm: React.FC<QuickPartyFormProps> = ({
  open,
  onOpenChange,
  onCreated,
}) => {
  const queryClient = useQueryClient();

  const form = useForm<QuickPartyFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      phone: "",
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: PartyCreate) => createParty(data),
    onSuccess: (newParty) => {
      toast.success(`Party "${newParty.name}" created successfully!`);
      onCreated(newParty.id, newParty);
      queryClient.invalidateQueries({ queryKey: ["parties"] });
      form.reset();
      onOpenChange(false);
    },
    onError: (error: Error) => {
      let errorMsg = error.message || "Unknown error";
      if (error instanceof AxiosError && error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      }
      toast.error(`Failed to create party: ${errorMsg}`);
    },
  });

  const handleSubmit = form.handleSubmit((data) => {
    const payload = {
      name: data.name,
      phone: data.phone || null,
      party_type: "customer",
    } as unknown as PartyCreate;
    createMutation.mutate(payload);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[350px]">
        <DialogHeader>
          <DialogTitle>Quick Party Creation</DialogTitle>
          <DialogDescription>
            Create a new customer with basic details.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name *</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="e.g. John Doe"
                      autoFocus
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ""}
                      placeholder="e.g. 9876543210"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create Party"
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default QuickPartyForm;
